/**
 * GenericWindowContent - Fallback viewer for entity types without specialized viewers
 */

import { FileJson, Tag, Info } from 'lucide-react';
import type { TiledWindow } from '@/types/tiled-window';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ENTITY_TYPE_LABELS } from '@/types/tiled-window';

interface GenericWindowContentProps {
    window: TiledWindow;
}

export function GenericWindowContent({ window }: GenericWindowContentProps) {
    const { entityType, entityName, data } = window;
    
    const typeLabel = ENTITY_TYPE_LABELS[entityType] || entityType;

    return (
        <ScrollArea className="h-full">
            <div className="space-y-6 p-6">
                {/* Header */}
                <div>
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                            <Info className="h-6 w-6 text-slate-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">{entityName}</h2>
                            <Badge variant="outline">{typeLabel}</Badge>
                        </div>
                    </div>
                </div>

                {/* Raw Data */}
                {data && Object.keys(data).length > 0 && (
                    <div>
                        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase text-slate-500">
                            <FileJson size={12} />
                            Data
                        </h3>
                        <pre className="overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">
                            {JSON.stringify(data, null, 2)}
                        </pre>
                    </div>
                )}

                {/* Empty state */}
                {(!data || Object.keys(data).length === 0) && (
                    <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                        <p className="text-sm text-slate-500">No data available</p>
                    </div>
                )}
            </div>
        </ScrollArea>
    );
}

